import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { BRACKET_CONFIG } from '@/lib/constants';

const useReducedMotion = () =>
  useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches, [])

interface ConfidenceSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showPreview?: boolean;
  className?: string;
}

const ConfidenceSlider: React.FC<ConfidenceSliderProps> = ({
  value,
  onChange,
  disabled = false,
  size = 'md',
  showPreview = true,
  className = '',
}) => {
  const { min, max } = BRACKET_CONFIG.confidence;

  // Local state for smooth dragging — only commit on release
  const [localValue, setLocalValue] = useState(value);
  const isDragging = useRef(false);

  // Sync from parent when not dragging
  useEffect(() => {
    if (!isDragging.current) setLocalValue(value);
  }, [value]);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    isDragging.current = true;
    setLocalValue(Number(e.target.value));
  }, []);

  const handleCommit = useCallback(() => {
    isDragging.current = false;
    if (localValue !== value) onChange(localValue);
  }, [localValue, value, onChange]);

  const handleTickClick = useCallback((tick: number) => {
    if (disabled) return;
    setLocalValue(tick);
    onChange(tick);
  }, [disabled, onChange]);

  // Color based on value
  const getColor = (val: number) => {
    const pct = ((val - min) / (max - min)) * 100;
    if (pct <= 33) return `rgb(239, ${68 + Math.round(119 * (pct / 33))}, 68)`;
    if (pct <= 66) return `rgb(${239 - Math.round(119 * ((pct - 33) / 33))}, 187, 68)`;
    return `rgb(0, 208, 132)`;
  };

  const sizeConfig = {
    sm: { height: 'h-2', thumbSize: 'w-4 h-4', fontSize: 'text-xs' },
    md: { height: 'h-3', thumbSize: 'w-5 h-5', fontSize: 'text-sm' },
    lg: { height: 'h-4', thumbSize: 'w-6 h-6', fontSize: 'text-base' },
  };

  const reducedMotion = useReducedMotion();
  const config = sizeConfig[size];
  const currentColor = getColor(localValue);
  const percentage = ((localValue - min) / (max - min)) * 100;

  return (
    <div className={`w-full space-y-2 ${className}`}>
      {/* Value display */}
      <div className="flex items-center justify-between">
        <span className={`font-medium text-text-primary ${config.fontSize}`}>
          Confidence: {localValue}
        </span>
        {showPreview && (
          <span className={`text-text-secondary ${config.fontSize}`}>
            {localValue} pts
          </span>
        )}
      </div>

      {/* Slider */}
      <div className="relative">
        <div className={`w-full bg-dark-400 rounded-full ${config.height}`}>
          <div
            className={`bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full ${config.height}`}
            style={{ width: `${percentage}%`, transition: reducedMotion || isDragging.current ? 'none' : 'width 0.15s ease-out' }}
          />
        </div>

        <div
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 
                     ${config.thumbSize} border-2 border-white rounded-full shadow-lg
                     ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                     focus:outline-none`}
          style={{
            left: `${percentage}%`,
            backgroundColor: currentColor,
            boxShadow: disabled ? undefined : '0 0 0 2px rgba(255, 255, 255, 0.2)',
            transition: reducedMotion || isDragging.current ? 'none' : 'left 0.15s ease-out',
          }}
        />

        <input
          type="range"
          min={min}
          max={max}
          value={localValue}
          onInput={handleInput}
          onChange={handleInput}
          onMouseUp={handleCommit}
          onTouchEnd={handleCommit}
          onKeyUp={handleCommit}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          aria-label={`Confidence level: ${localValue}`}
        />
      </div>

      {/* Tick marks */}
      <div className="flex justify-between">
        {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((tick) => (
          <button
            key={tick}
            onClick={() => handleTickClick(tick)}
            disabled={disabled}
            className={`text-xs text-text-muted hover:text-text-secondary transition-colors
                       ${localValue === tick ? 'text-primary-400 font-semibold' : ''}
                       ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {tick}
          </button>
        ))}
      </div>

      <div className="flex justify-between text-xs text-text-muted">
        <span>Low Risk</span>
        <span>Medium Risk</span>
        <span>High Risk</span>
      </div>
    </div>
  );
};

export default ConfidenceSlider;
